import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/lib/supabase';

// -----------------------------------------------------------------------
// Production forms (migration 112)
// -----------------------------------------------------------------------

export interface ProductionFormComponentRow {
  id: string;
  item_id: string;
  bom_line_id: string | null;
  expected_quantity: number | null;
  quantity: number;
  unit_cost_cents: number | null;
  position: number;
  items: { id: string; sku: string; name: string; base_uom: string } | null;
  locations: { id: string; name: string; code: string } | null;
  lots: { lot_number: string } | null;
}

/** One submitted production form — the unit the Production table lists. */
export interface ProductionFormRow {
  id: string;
  production_number: string;
  item_id: string;
  bom_version_id: string | null;
  quantity: number;
  scrap_quantity: number;
  produced_at: string;
  produced_by: string | null;
  component_cost_cents: number;
  unit_cost_cents: number | null;
  notes: string | null;
  created_by: string | null;
  created_at: string;
  items: { id: string; sku: string; name: string; base_uom: string } | null;
  locations: { id: string; name: string; code: string } | null;
  lots: { lot_number: string } | null;
  workstations: { id: string; name: string } | null;
  builder: { id: string; full_name: string | null; email: string } | null;
  bom_versions: { version: number } | null;
  production_form_components: ProductionFormComponentRow[];
}

export function useProductionForms() {
  return useQuery({
    queryKey: ['production-forms'],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('production_forms' as never)
        .select(
          '*, items(id, sku, name, base_uom), locations:to_location_id(id, name, code), lots(lot_number), workstations(id, name), builder:produced_by(id, full_name, email), bom_versions(version), production_form_components(id, item_id, bom_line_id, expected_quantity, quantity, unit_cost_cents, position, items(id, sku, name, base_uom), locations:from_location_id(id, name, code), lots(lot_number))',
        )
        .order('produced_at', { ascending: false })
        .order('created_at', { ascending: false })
        .limit(2000);
      if (error) throw error;
      return (data as unknown as ProductionFormRow[]).map((row) => ({
        ...row,
        production_form_components: [...(row.production_form_components ?? [])].sort(
          (a, b) => a.position - b.position,
        ),
      }));
    },
  });
}

export interface ProductionComponentInput {
  item_id: string;
  quantity: number;
  from_location_id: string;
  bom_line_id?: string | null;
  expected_quantity?: number | null;
  lot_number?: string | null;
}

export interface SubmitProductionFormInput {
  itemId: string;
  quantity: number;
  scrapQuantity: number;
  toLocationId: string;
  lotNumber?: string | null;
  workstationId?: string | null;
  producedAt: string;
  producedBy?: string | null;
  bomVersionId?: string | null;
  notes?: string | null;
  components: ProductionComponentInput[];
}

/**
 * Submit a production form via `submit_production_form`. The header posts
 * a 'production_produce' stock movement for the good units and every
 * component line posts a 'production_consume' movement, so on-hand for
 * both the finished good and its components updates in one transaction.
 */
export function useSubmitProductionForm() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (input: SubmitProductionFormInput) => {
      const { data, error } = await supabase.rpc('submit_production_form' as never, {
        p_form: {
          item_id: input.itemId,
          quantity: input.quantity,
          scrap_quantity: input.scrapQuantity,
          to_location_id: input.toLocationId,
          lot_number: input.lotNumber ?? null,
          workstation_id: input.workstationId ?? null,
          produced_at: input.producedAt,
          produced_by: input.producedBy ?? null,
          bom_version_id: input.bomVersionId ?? null,
          notes: input.notes ?? null,
        },
        p_components: input.components,
      } as never);
      if (error) throw error;
      return data as unknown as string;
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['production-forms'] });
      qc.invalidateQueries({ queryKey: ['stock-movements'] });
      qc.invalidateQueries({ queryKey: ['inventory-levels'] });
      qc.invalidateQueries({ queryKey: ['lots'] });
    },
  });
}
